import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from '../../styles/ReporteSeguroAsesoresStyles/ReporteSeguroAsesores.module.css';
import Sidebar, { useSidebar } from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import LoaderOverlay from '../../components/LoaderOverlay';
import FiltrosReporteSegurosAsesores from './FiltrosReporteSegurosAsesores';
import TablaReporteSegurosAsesores from './TablaReporteSegurosAsesores';
import { getReporteSegurosAsesores } from '../../api/reporteSegurosAsesores';

const PAGE_SIZE = 15;

function ReporteSeguroAsesoresScreen() {
  const { isCollapsed } = useSidebar();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null); // { resultados, total, page, page_size, total_pages }
  const [contexto, setContexto] = useState({});
  const [page, setPage] = useState(1);

  // Filtros aplicados a la última generación (los que viajan al backend).
  // Se separan de los filtros del formulario para evitar refetch cuando el
  // usuario solo edita el select sin presionar "Generar".
  const [appliedFilters, setAppliedFilters] = useState(null);

  const [filters, setFilters] = useState({
    id_campus: '',
  });

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const fetchPage = useCallback(async (filtros, pageNum) => {
    setLoading(true);
    setError('');
    try {
      const data = await getReporteSegurosAsesores({
        id_campus: filtros.id_campus || undefined,
        page: pageNum,
        page_size: PAGE_SIZE,
      });
      setReportData(data);
      // El backend puede haber clampeado la página (p.ej. fuera de rango)
      if (data && typeof data.page === 'number' && data.page !== pageNum) {
        setPage(data.page);
      }
    } catch (err) {
      console.error('[ReporteSeguroAsesoresScreen] Error:', err);
      setError(err.message || 'No se pudo obtener el reporte.');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleGenerate = useCallback(async () => {
    const filtros = { id_campus: filters.id_campus };
    setAppliedFilters(filtros);
    setPage(1);
    await fetchPage(filtros, 1);
  }, [filters, fetchPage]);

  // Recargar al cambiar de página (solo si ya se generó el reporte)
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (!appliedFilters) return;
    fetchPage(appliedFilters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handlePageChange = useCallback((next) => {
    setPage(next);
  }, []);

  return (
    <div className={styles.mainContainer}>
      <div className={`${styles.layout} ${isCollapsed ? styles.sidebarCollapsedLayout : ''}`}>
        <div className={styles.sidebarColumn}>
          <Sidebar />
        </div>
        <div className={styles.contentColumn}>
          <main className={styles.mainContent}>
            <HeaderGlobal title="Reporte Seguros Asesores" />
            <div className={styles.contentArea}>
              <div className={styles.contentWrapper} style={{ position: 'relative' }}>
                <LoaderOverlay visible={loading} label="Generando reporte..." />

                <FiltrosReporteSegurosAsesores
                  filters={filters}
                  onChange={handleFilterChange}
                  onGenerate={handleGenerate}
                  loading={loading}
                  onContextResolved={setContexto}
                />

                {error && (
                  <div
                    style={{
                      background: '#fdecea',
                      color: '#c5221f',
                      padding: '10px 14px',
                      borderRadius: 4,
                      marginBottom: 16,
                      fontSize: 13,
                    }}
                  >
                    {error}
                  </div>
                )}

                {reportData ? (
                  <div className={styles.reportContainer}>
                    <TablaReporteSegurosAsesores
                      resultados={reportData.resultados || []}
                      contexto={contexto}
                      page={reportData.page || page}
                      pageSize={reportData.page_size || PAGE_SIZE}
                      total={reportData.total || 0}
                      totalPages={reportData.total_pages || 1}
                      onPageChange={handlePageChange}
                      appliedFilters={appliedFilters}
                      loading={loading}
                    />
                  </div>
                ) : (
                  <div className={styles.reportContainer}>
                    <div className={styles.emptyState}>
                      <div className={styles.emptyIcon}>🛡️</div>
                      <p className={styles.emptyText}>Sin reporte generado</p>
                      <p className={styles.emptySubText}>
                        Selecciona los filtros y presiona <strong>Generar Reporte</strong>.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default ReporteSeguroAsesoresScreen;

